import RegioChecklist from '@uikit/components/RegioChecklist/RegioChecklist';
import RegioContactSection from '@uikit/components/RegioContactSection/RegioContactSection';
import RegioFact from '@uikit/components/RegioFact/RegioFact';
import RegioKeyVisualSection from '@uikit/components/RegioKeyVisualSection/RegioKeyVisualSection';
import RegioStage from '@uikit/components/RegioStage/RegioStage';
import RegioTeaser from '@uikit/components/RegioTeaser/RegioTeaser';
import Section from '@uikit/components/Section/Section';
import Spacer from '@uikit/components/Spacer/Spacer';
import { formatNumber } from '@uikit/helpers/formatNumber';
import getCheapestTariff from '@uikit/helpers/getCheapestTariff';
import getTariffSavingsAmount from '@uikit/helpers/getTariffSavingsAmount';
import hasSufficientSavingsAmount from '@uikit/helpers/isTariffCheaperThanBaseProvider';
import { useCallback, useState } from 'react';
import { ClickableAction } from '../Clickable/consts';
import ComponentList from '../ComponentList/ComponentList';
import { JustifyContent } from '../ComponentList/consts';
import Container from '../Container/Container';
import { ContainerSize } from '../Container/consts';
import Headline from '../Headline/Headline';
import { HeadlineAlign, HeadlineColor } from '../Headline/consts';
import Link from '../Link/Link';
import RegioTabs from '../RegioTabs/RegioTabs';
import RegioTariffWrapper from '../RegioTariffWrapper/RegioTariffWrapper';
import Teaser from '../Teaser/Teaser';
import styles from './RegioPageContent.module.scss';
import {
  DEFAULT_IMAGES,
  PRODUCT_DETAILS,
  SAVINGS_AMOUNT_THRESHOLD,
  relatedLinks,
} from './consts';
import { createGermanAbsoluteLinks } from './helpers';
import type { RegioPageContentProps } from './types';

const RegioPageContentPower = ({
  baseProviderTariff,
  cityName,
  cityKey,
  headline,
  imageSustainability,
  lastUpdated,
  nearestCities,
  population,
  productType,
  subText,
  tariffs,
  fallbackTariffs,
  titleSustainability,
  textSustainability,
  textSustainabilityEnercity,
  zipCode,
  streetName,
  streetNumber,
  regioProductPaths,
  configuratorUrl,
  tippsHeadline,
  tippsSubText,
  tipp1,
  tipp2,
  tippHeadline1,
  tippHeadline2,
}: RegioPageContentProps) => {
  const [selectedConsumption, setSelectedConsumption] = useState<number>(
    PRODUCT_DETAILS[productType][0].consumption
  );
  const cheapestTariff = getCheapestTariff(tariffs);
  const savingsAmount = getTariffSavingsAmount(
    cheapestTariff,
    baseProviderTariff
  );
  const isCheaperThanBaseProvider = hasSufficientSavingsAmount(
    savingsAmount,
    SAVINGS_AMOUNT_THRESHOLD
  );

  const regioStageSubtext = isCheaperThanBaseProvider
    ? subText
    : `enercity - Ihr Stromanbieter für ${cityName}.`;

  const tariffComparisonTitle = isCheaperThanBaseProvider
    ? 'Vertrauen ist gut - vergleichen ist besser!'
    : `Unsere Stromtarife für ${cityName}`;

  const tariffComparisonSubtitle = isCheaperThanBaseProvider
    ? `Sparen Sie bares Geld in ${cityName}! In unserer Beispielrechnung zeigen wir Ihnen, was für Sie herausspringt.`
    : `Günstige Strompreise für Ihr Zuhause mit 100 % Ökostrom in Stadtwerke-Qualität!`;

  const handleChange = useCallback(
    (value: string) => {
      setSelectedConsumption(+value);
    },
    [setSelectedConsumption]
  );

  return (
    <div className={styles.base}>
      <Section>
        <Container>
          <RegioStage
            city={cityName}
            description={regioStageSubtext}
            headline={headline}
            configuratorUrl={configuratorUrl}
            stageBoxTitle={`Jetzt Stromtarif für ${cityName} berechnen!`}
          />
        </Container>
      </Section>
      <Spacer defaultSize={2} sm={3} />
      <Section hasPadding={false} hasBottomSpace={false}>
        <Container size={ContainerSize.Max}>
          <Headline
            subText={tariffComparisonSubtitle}
            headlineColor={HeadlineColor.Gradient}
            className={styles.headline}
          >
            {tariffComparisonTitle}
          </Headline>
          <RegioTabs
            onValueChange={handleChange}
            defaultValue={PRODUCT_DETAILS[productType]
              ?.at(0)
              ?.consumption?.toString()}
            id="vergleich"
          >
            <RegioTabs.List>
              {PRODUCT_DETAILS[productType].map(
                ({ label, consumption, icon }) => (
                  <RegioTabs.Trigger
                    value={consumption?.toString()}
                    iconVariant={icon}
                    key={`navitem-${consumption}`}
                  >
                    <span className={styles.tabTitle}>
                      {label}
                      <span className={styles.consumption}>
                        ({`${formatNumber(consumption)} kWh`})
                      </span>
                    </span>
                  </RegioTabs.Trigger>
                )
              )}
            </RegioTabs.List>
            {PRODUCT_DETAILS[productType].map(({ consumption }) => {
              return (
                <RegioTabs.Content
                  value={consumption.toString()}
                  key={`content-${consumption}`}
                >
                  <RegioTariffWrapper
                    city={cityName}
                    cityKey={cityKey}
                    configuratorUrl={configuratorUrl}
                    consumption={selectedConsumption}
                    lastUpdated={lastUpdated}
                    productType={productType}
                    streetName={streetName}
                    streetNumber={streetNumber}
                    fallbackTariffs={fallbackTariffs}
                    zipCode={zipCode}
                  />
                </RegioTabs.Content>
              );
            })}
          </RegioTabs>
        </Container>
      </Section>
      <RegioKeyVisualSection
        image={imageSustainability}
        title={
          titleSustainability ||
          `Klimaschutz und Nachhaltigkeit in ${cityName} - Hier fühlt sich enercity als Stromanbieter wohl!`
        }
        badgeText="**100 %** Ökostrom"
        mainText={
          textSustainability ||
          `Wir als zukunftsorientierter Stromanbieter in ${cityName} beliefern schon seit 2018 all unsere Kunden in Deutschland mit 100 % Ökostrom. Enercity ist also eine echte Alternative zu den Stadtwerken ${cityName}. Unseren grünen Strom garantieren wir Ihnen mit unserer TÜV-Süd-Zertifizierung, die bestätigt, dass unser Strom ausschließlich aus erneuerbaren Energiequellen stammt. Übrigens bauen wir als Top Öko-Stromanbieter immer mehr Produktionseinheiten aus, mit denen wir selbst aktiv Ökostrom erzeugen können und zurück in das Strom-Netz einspeisen. Entdecken Sie also schon heute mit enercity die Strom-Welt von morgen und wechseln ganz einfach Ihren Stromanbieter in ${cityName}.`
        }
        highlightedText={
          textSustainabilityEnercity ||
          'Gemeinsam gestalten wir die Energiewende!'
        }
        link={configuratorUrl}
        linkText="Jetzt wechseln"
        id="klimaschutz"
      />
      <Section sectionId="tipps">
        <Container className={styles.regioComparisonTipsContainer}>
          <Headline
            align={HeadlineAlign.Left}
            subText={
              tippsSubText ||
              `Es gibt in ${cityName} unzählige <strong>Stromanbieter</strong>${
                baseProviderTariff
                  ? ` und natürlich auch einen örtlichen <strong>Grundversorger - ${baseProviderTariff.providerName}</strong>`
                  : ''
              }. Wenn Sie das Gefühl
          haben, bei der Recherche auf <strong>Vergleichsportalen</strong> den Überblick über
          aktuelle Strompreise und diverse <strong>Vergleichskriterien</strong> zu verlieren,
          haben wir zwei Tipps für Sie:`
            }
          >
            {tippsHeadline || `Stromvergleich für ${cityName}`}
          </Headline>
          <ComponentList justifyChild={JustifyContent.Stretch}>
            <Teaser
              headline={tippHeadline1 || 'Tipp 1'}
              icon="user/information"
              actionType={ClickableAction.None}
              headlineTag="div"
              isIcon
            >
              {tipp1 ||
                `Vergleichen Sie immer den reinen Grund- & Arbeitspreis für Ihren
              neuen Stromtarif. Multiplizieren Sie den Arbeitspreis mit Ihrem
              Jahresverbrauch und addieren Sie den Grundpreis pro Jahr hinzu. So
              erhalten Sie Ihren Jahrespreis und können die Kosten pro Jahr bei
              verschiedenen Anbietern gegenüberstellen.`}
            </Teaser>
            <Teaser
              headline={tippHeadline2 || 'Tipp 2'}
              icon="user/information"
              actionType={ClickableAction.None}
              headlineTag="div"
              isIcon
            >
              {tipp2 ||
                `Bei einem möglichen Bonus gilt es folgendes zu beachten: Überprüfen Sie die Bedingungen für die Bonusauszahlung genau. Es ist möglich, dass ein Bonus für Ihre Stromanmeldung erst nach einer Mindestvertragslaufzeit gewährt wird und Ihr Stromvertrag in ${cityName} im Zeitvergleich gesehen doch teurer wird als gedacht.`}
            </Teaser>
          </ComponentList>
        </Container>
      </Section>
      <Section>
        <Container className={styles.regioFirstFactContainer}>
          <RegioFact>
            Mit geballter <strong>Energie</strong> für alle{' '}
            <strong>{population}</strong> Bürger:innen von{' '}
            <strong>{cityName}</strong> vor Ort.
          </RegioFact>
        </Container>
      </Section>
      <Section>
        <Container className={styles.regioTeaserContainer}>
          <RegioTeaser
            image={DEFAULT_IMAGES.TEASER_MORE_INFO}
            headline="enercity - bundesweit verfügbar"
            description={`Als einer der zehn größten Energieversorger Deutschlands versorgen wir Privat- und Gewerbekunden in ${cityName} <strong>als zuverlässiger Stromanbieter. Mittlerweile sind wir sogar als einer der Top 5 Ökostromanbieter mit Stadtwerke-Qualität bundesweit verfügbar.</strong> Entstanden aus den Stadtwerken Hannover versorgen wir unsere Heimat bereits seit über 125 Jahren mit Energie.`}
            link="/privatkunden/produkte/strom"
            linkText="Jetzt Strom erleben & enercity kennenlernen"
          />
          <RegioTeaser
            image={DEFAULT_IMAGES.TEASER_CONTACT}
            headline={`Immer erreichbar - auch in ${cityName}`}
            description={`Ihre Bedürfnisse liegen uns am Herzen - deshalb sind wir jederzeit für Sie da. Sie erreichen uns per <strong>Chat, Telefon oder Mail</strong>. Jederzeit für Sie in ${cityName}.`}
            link="https://support.enercity.de"
            linkText="Kontaktieren Sie uns!"
          />
        </Container>
      </Section>
      <Section sectionId="checkliste">
        <Container>
          <RegioChecklist
            image={DEFAULT_IMAGES.CHECKLIST}
            mainTitle="An alles gedacht? Die Checkliste für Ihren Wechsel"
            mainDescription={`Sie wollen vor Ihrem Stromanbieterwechsel in ${cityName} noch die Preise vergleichen? Gute Idee. Wir zeigen Ihnen, worauf Sie bei der Wahl eines neuen Stromanbieters achten sollten.`}
            boxTitle="Alles klar soweit?"
            boxDescription={`Sind alle Fragen beantwortet? Prima. Dann steht einem Wechsel zu enercity mit Strom in Stadtwerkequalität nichts mehr im Wege.`}
            listItems={[
              'Falls Ihnen stabile Preise wichtig sind: Achten Sie auf die Preisgarantie.',
              'Vergleichen Sie den Grund- bzw. Arbeitspreis Ihrer bevorzugten Lieferanten',
              'Ist der Anbieter neu am Markt oder schon lange etabliert?',
              'Sparfüchse aufgepasst: mit Boni und Prämien sparen Sie bares Geld!',
              'Ökostrom: erhalten Sie nachhaltige Energie?',
            ]}
            link={configuratorUrl}
            linkText="Jetzt wechseln"
          />
        </Container>
      </Section>
      <Section>
        <Container className={styles.regioSecondFactContainer}>
          <RegioFact>
            Das ist <strong>enercity - 125 Jahre</strong> Erfahrung.{' '}
            <strong>350.000</strong> zufriedene Kund:innen.
            <br />
            <strong>100 %</strong> Ökostrom.
          </RegioFact>
        </Container>
      </Section>
      <RegioContactSection title={`Wir sind für Sie in ${cityName} da!`}>
        <RegioContactSection.RichText>
          Unsere günstigen Ökostromtarife finden Sie nicht nur in {cityName},
          sondern auch in {createGermanAbsoluteLinks(nearestCities)}, sowie in
          vielen weiteren Städten.
        </RegioContactSection.RichText>
        <RegioContactSection.RichText>
          Neben günstigem Strom aus 100 % {relatedLinks.ecoPower} bieten wir als
          lokaler Stromanbieter bspw. auch {relatedLinks.gas},{' '}
          {relatedLinks.solar}, {relatedLinks.emob}, {relatedLinks.heat} sowie
          hilfreiche Tipps für den {relatedLinks.powerComparison}.
          Selbstverständlich können Sie bei enercity auch direkt den passenden{' '}
          <Link to={regioProductPaths.gas}>Gastarif für {cityName}</Link>{' '}
          abschließen.
        </RegioContactSection.RichText>
      </RegioContactSection>
    </div>
  );
};

export default RegioPageContentPower;
